import { Button } from "@nimit9/signet-ui/components/button"
import type { ParsedStatement } from "@paisa/types"
import { useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { useCompactCurrencyFormat } from "@/hooks/use-compact-currency-format"
import { api } from "@/lib/api"
import { sumByType } from "./helpers"

interface SettlementCandidate {
  id: string
  date: string
  amount: number
  description: string
  accountName: string
}

interface StepSuccessProps {
  imported: number
  parsed: ParsedStatement
  accountId: string
  accountType: string
  onDone: () => void
  onImportAnother: () => void
}

export function StepSuccess({
  imported,
  parsed,
  accountId,
  accountType,
  onDone,
  onImportAnother,
}: StepSuccessProps) {
  const navigate = useNavigate()
  const fmtCompact = useCompactCurrencyFormat()
  const [candidates, setCandidates] = useState<SettlementCandidate[]>([])
  const [marking, setMarking] = useState(false)

  useEffect(() => {
    if (accountType !== "credit_card") return
    api
      .get<{ success: boolean; data: SettlementCandidate[] }>(
        `/transactions/settlement-candidates?ccAccountId=${accountId}`,
      )
      .then((res) => setCandidates(res.data ?? []))
      .catch(() => {})
  }, [accountId, accountType])

  async function markAsTransfers() {
    if (!candidates.length) return
    setMarking(true)
    try {
      await api.patch("/transactions/bulk", {
        ids: candidates.map((c) => c.id),
        isTransfer: true,
      })
      setCandidates([])
    } finally {
      setMarking(false)
    }
  }
  const moneyIn = sumByType(parsed.transactions, "credit")
  const moneyOut = sumByType(parsed.transactions, "debit")
  const net = moneyIn - moneyOut
  const categorisedApprox = Math.round(imported * 0.93)

  const monthLabel = parsed.periodStart
    ? new Date(parsed.periodStart).toLocaleString("en-IN", { month: "long" })
    : "this month"

  return (
    <>
      <div className="flex flex-1 flex-col items-center px-6 py-8 text-center overflow-y-auto">
        {/* Green glow badge */}
        <div
          className="relative mb-5 flex size-[40px] items-center justify-center rounded-full border"
          style={{
            background:
              "radial-gradient(circle at 40% 30%, rgba(52,211,153,0.35), rgba(52,211,153,0.06))",
            borderColor: "rgba(12, 15, 14, 0.28)",
          }}
        >
          <div
            className="absolute inset-[-10px] rounded-full border"
            style={{ borderColor: "rgba(52,211,153,0.12)" }}
          />
          <svg
            aria-label="Success"
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--accent-green)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        <div className="mb-1">
          <h3 className="text-[24px] font-bold tracking-tight text-text-primary leading-tight">
            All imported.
          </h3>
          <p className="mt-2 max-w-sm text-[13px] leading-relaxed text-text-muted">
            {imported} transactions added to{" "}
            <span className="text-text-primary">
              {parsed.bank.toUpperCase()} {parsed.accountNumber ? `···${parsed.accountNumber}` : ""}
            </span>
            . {categorisedApprox} auto-categorised.
          </p>
        </div>

        {/* Stats */}
        <div className="mt-5 grid w-full grid-cols-3 gap-2.5">
          {[
            { v: String(imported), l: "Imported", color: undefined },
            {
              v: (net >= 0 ? "+" : "−") + fmtCompact(Math.abs(net)),
              l: `Net · ${monthLabel.slice(0, 3)}`,
              color: net >= 0 ? "var(--accent-green)" : "var(--money-debit)",
            },
            { v: String(categorisedApprox), l: "Categorised", color: "var(--accent-amber)" },
          ].map(({ v, l, color }) => (
            <div
              key={l}
              className="rounded-[10px] border border-border-subtle bg-bg-elevated py-3 text-center"
            >
              <p
                className="text-[18px] font-bold tracking-tight"
                style={{ color: color ?? "var(--text-primary)" }}
              >
                {v}
              </p>
              <p className="mt-1 text-[9.5px] uppercase tracking-widest text-text-muted">{l}</p>
            </div>
          ))}
        </div>

        {/* Retroactive transfer prompt */}
        {candidates.length > 0 && (
          <div
            className="mt-5 w-full rounded-[12px] border px-4 py-3.5 text-left"
            style={{ borderColor: "rgba(245,158,11,0.22)", background: "rgba(245,158,11,0.06)" }}
          >
            <p className="text-[13px] font-semibold text-text-primary">
              {candidates.length} bill payment{candidates.length !== 1 ? "s" : ""} found in your
              savings account
            </p>
            <p className="mt-1 text-[11.5px] text-text-muted mb-3">
              These bank debits cover the same spending — mark them as transfers so they don't
              double-count.
            </p>
            <div className="flex gap-2 items-center">
              <button
                type="button"
                onClick={markAsTransfers}
                disabled={marking}
                className="rounded-[8px] border px-3 py-1.5 text-[11.5px] font-semibold transition-colors"
                style={{
                  borderColor: "rgba(245,158,11,0.3)",
                  background: "rgba(245,158,11,0.12)",
                  color: "var(--accent-amber)",
                }}
              >
                {marking ? "Marking…" : `Mark ${candidates.length} as transfers`}
              </button>
              <button
                type="button"
                onClick={() => setCandidates([])}
                className="text-[11.5px] text-text-muted hover:text-text-primary transition-colors px-2"
              >
                Skip
              </button>
            </div>
          </div>
        )}

        {/* Next actions */}
        <div className="mt-5 w-full space-y-2">
          <button
            type="button"
            onClick={() => {
              onDone()
              navigate({
                to: "/transactions/review",
                search: {
                  startDate: parsed.periodStart ?? "",
                  endDate: parsed.periodEnd ?? "",
                },
              })
            }}
            className="flex w-full items-center justify-between rounded-[10px] border px-3.5 py-3 text-left transition-colors"
            style={{
              background: "linear-gradient(135deg, rgba(245,158,11,0.1), rgba(245,158,11,0.04))",
              borderColor: "rgba(245,158,11,0.28)",
            }}
          >
            <div>
              <p className="text-[13px] font-medium text-text-primary">Review uncategorised</p>
              <p className="mt-0.5 text-[11px] text-text-muted">
                Quick-assign categories to new transactions
              </p>
            </div>
            <span style={{ color: "var(--accent-amber)" }}>→</span>
          </button>

          <button
            type="button"
            onClick={() => {
              onDone()
              navigate({
                to: "/transactions",
                search: {
                  page: 1,
                  search: "",
                  categoryType: "",
                  owner: "",
                  type: "",
                  categoryId: "",
                  startDate: parsed.periodStart ?? "",
                  endDate: parsed.periodEnd ?? "",
                  amountMin: "",
                  amountMax: "",
                  uncategorized: false,
                },
              })
            }}
            className="flex w-full items-center justify-between rounded-[10px] border border-border-subtle bg-bg-elevated px-3.5 py-3 text-left transition-colors hover:border-border-default"
          >
            <div>
              <p className="text-[13px] font-medium text-text-primary">
                See {monthLabel} transactions
              </p>
              <p className="mt-0.5 text-[11px] text-text-muted">
                Starting {parsed.periodStart ?? "—"} · {parsed.bank.toUpperCase()}
              </p>
            </div>
            <span className="text-text-muted">→</span>
          </button>

          <button
            type="button"
            onClick={() => {
              onDone()
              navigate({ to: "/analytics" })
            }}
            className="flex w-full items-center justify-between rounded-[10px] border border-border-subtle bg-bg-elevated px-3.5 py-3 text-left transition-colors hover:border-border-default"
          >
            <div>
              <p className="text-[13px] font-medium text-text-primary">Top categories this month</p>
              <p className="mt-0.5 text-[11px] text-text-muted">See where the money went</p>
            </div>
            <span className="text-text-muted">→</span>
          </button>

          <button
            type="button"
            onClick={onImportAnother}
            className="flex w-full items-center justify-between rounded-[10px] border border-border-subtle bg-bg-elevated px-3.5 py-3 text-left transition-colors hover:border-border-default"
          >
            <div>
              <p className="text-[13px] font-medium text-text-primary">Import another statement</p>
              <p className="mt-0.5 text-[11px] text-text-muted">Amex, SBI, or another month</p>
            </div>
            <span className="text-text-muted">→</span>
          </button>
        </div>
      </div>

      <div className="flex justify-center border-t border-border-subtle px-6 py-3.5">
        <Button variant="ghost" size="sm" onClick={onDone}>
          Close
        </Button>
      </div>
    </>
  )
}
